<template>
  <div class="permissions-container">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
      <p class="mt-4 text-body-2">Loading permissions...</p>
    </div>
    
    <!-- Permissions Content -->
    <v-container v-else fluid class="pr-0 pl-0 py-0">
      <!-- Dynamic Permission Categories -->
      <v-row 
        v-for="category in permissionCategories" 
        :key="category.uuid"
        class="mb-3"
      >
        <v-col cols="12" class="py-0">
          <v-card class="permission-card" flat>
            <v-card-title class="section-title pa-3 pb-2">
              <v-icon color="primary" class="mr-2" size="20">{{ category.icon }}</v-icon>
              <span class="text-subtitle-1 font-weight-medium">{{ category.displayName }}</span>
            </v-card-title>
            
            <v-card-text class="pa-3 pt-0">
              <div class="permission-list">
                
                <v-expansion-panels
                  multiple
                  flat
                  class="permission-expansion-panels"
                >
                  <v-expansion-panel
                    v-for="group in category.groups" 
                    :key="group.uuid"
                    class="permission-expansion-panel"
                  >
                    <v-expansion-panel-header class="permission-panel-header">
                      <div class="permission-header-content">
                        <div class="permission-info">
                          <span class="permission-label text-body-2">
                            {{ group.displayName }}
                          </span>
                        </div>
                      </div>
                    </v-expansion-panel-header>
                    
                    <v-expansion-panel-content class="permission-panel-content">
                      <div class="sub-permissions">
                        <div 
                          v-for="claim in group.claims" 
                          :key="claim.uuid"
                          class="sub-permission-item"
                        >
                          <v-checkbox
                            :input-value="isClaimSelected(claim.uuid)"
                            @change="toggleClaimByUuid(claim.uuid, $event)"
                            :label="claim.displayName"
                            dense
                            hide-details
                            color="primary"
                            class="sub-permission-checkbox"
                          />
                        </div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
    </v-container>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex';


export default {
  name: "GroupPermissions",
  props: {
    form: {
      type: Object,
      required: true,
    },
    groupUuid: {
      type: String,
      required: false,
    }
  },
  data() {
    const formPerms = (this.form && this.form.permissions) || {};
    return {
      localForm: {
        ...this.form,
        permissions: {
          selectedClaims: [],
          groupStates: {},
          ...formPerms
        }
      },
      loading: false,
    };
  },
  computed: {
    ...mapGetters('operators', [
      'getGroupPermissionsPerGroup',
      'getPermissions',
      'isLoading'
    ]),
    permissionCategories() {
      
      // 1. Try group-specific combined permissions
      let permissionsData = this.groupUuid
        ? this.getGroupPermissionsPerGroup(this.groupUuid)
        : [];
      
      // 2. Fallback: if no group-specific data, use raw global permissions (all unselected)
      if ((!permissionsData || permissionsData.length === 0) && this.getPermissions && this.getPermissions.length > 0) {
        permissionsData = this.getPermissions;
      }
      
      if (!permissionsData || !Array.isArray(permissionsData)) {
        return [];
      }
      // Map to UI structure
      return permissionsData.map(category => {
        return {
          uuid: category.operator_permission_category_uuid,
          name: category.operator_permission_category_name,
          displayName: this.getCategoryDisplayName(category.operator_permission_category_name),
          description: category.operator_permission_category_description,
          icon: this.getCategoryIcon(category.operator_permission_category_name),
          groups: (category.operator_permission_groups || []).map(group => ({
            uuid: group.operator_permission_group_uuid,
            name: group.operator_permission_group_name,
            displayName: group.operator_permission_group_display_name,
            description: group.operator_permission_group_description,
            claims: (group.operator_permission_claims || []).map(claim => ({
              uuid: claim.operator_permission_claim_uuid,
              displayName: claim.operator_permission_claim_display_name,
              technicalName: claim.operator_permission_claim_technical_name,
              description: claim.operator_permission_claim_description,
              selected: claim.selected || false
            }))
          }))
        };
      });
    }
  },
  async mounted() {
    // If no permissions data available yet, fetch them and set up for this group
    if (this.permissionCategories.length === 0) {
      this.loading = true;
      try {
        await this.$store.dispatch('operators/fetchPermissions');
        // If we have a groupUuid, combine permissions (all unselected for create)
        if (this.groupUuid) {
          const existing = this.getGroupPermissionsPerGroup(this.groupUuid);
          if (!existing || existing.length === 0) {
            await this.$store.dispatch('operators/combinePermissionsForGroup', {
              groupUuid: this.groupUuid,
              groupClaims: []
            });
          }
        }
      } catch (e) {
        this.addAlert({ type: 'error', message: 'Failed to load permissions. Please try again.' });
      } finally {
        this.loading = false;
      }
    }
  },
  methods: {
    ...mapActions({ addAlert: 'global/addAlert' }),
    // ...existing methods...

    getCategoryDisplayName(categoryName) {
      const displayNames = {
        'shop': 'Shop',
        'tbo': 'Terminal'
      };
      return displayNames[categoryName] || categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
    },

    getCategoryIcon(categoryName) {
      const icons = {
        'shop': 'mdi-store',
        'tbo': 'mdi-monitor'
      };
      return icons[categoryName] || 'mdi-cog';
    },

    // Check if a group has any enabled claims
    isGroupEnabled(categoryName, groupName) {
      if (!this.localForm.permissions.groupStates || !this.localForm.permissions.groupStates[categoryName]) {
        return false;
      }
      
      const category = this.permissionCategories.find(cat => cat.name === categoryName);
      if (!category) return false;
      
      const group = category.groups.find(grp => grp.name === groupName);
      if (!group) return false;
      
      // Check if any individual claims are selected
      return group.claims.some(claim => 
        this.localForm.permissions.selectedClaims.includes(claim.uuid)
      );
    },

    // Toggle all claims in a group
    toggleGroupPermissions(categoryName, groupName, enabled) {
      const category = this.permissionCategories.find(cat => cat.name === categoryName);
      if (!category) return;
      
      const group = category.groups.find(grp => grp.name === groupName);
      if (!group) return;
      
      // Set group master toggle state
      if (!this.localForm.permissions.groupStates[categoryName]) {
        this.$set(this.localForm.permissions.groupStates, categoryName, {});
      }
      this.$set(this.localForm.permissions.groupStates[categoryName], groupName, enabled);
      
      // Toggle all individual claims in the group
      group.claims.forEach(claim => {
        if (enabled) {
          // Add claim UUID if not already selected
          if (!this.localForm.permissions.selectedClaims.includes(claim.uuid)) {
            this.localForm.permissions.selectedClaims.push(claim.uuid);
          }
        } else {
          // Remove claim UUID if selected
          const index = this.localForm.permissions.selectedClaims.indexOf(claim.uuid);
          if (index > -1) {
            this.localForm.permissions.selectedClaims.splice(index, 1);
          }
        }
      });
      
      this.onPermissionChange();
    },

    // Check if a specific claim is selected by UUID
    isClaimSelected(claimUuid) {
      return this.localForm.permissions.selectedClaims && 
             this.localForm.permissions.selectedClaims.includes(claimUuid);
    },

    // Toggle individual claim by UUID
    toggleClaimByUuid(claimUuid, enabled) {
      if (!this.localForm.permissions.selectedClaims) {
        this.localForm.permissions.selectedClaims = [];
      }

      if (enabled) {
        // Add claim UUID if not already selected
        if (!this.localForm.permissions.selectedClaims.includes(claimUuid)) {
          this.localForm.permissions.selectedClaims.push(claimUuid);
        }
      } else {
        // Remove claim UUID if selected
        const index = this.localForm.permissions.selectedClaims.indexOf(claimUuid);
        if (index > -1) {
          this.localForm.permissions.selectedClaims.splice(index, 1);
        }
      }

      // Update group states based on selected claims
      this.updateGroupStatesFromClaims();
      this.onPermissionChange();
    },

    // Update group toggle states based on selected claims
    updateGroupStatesFromClaims() {
      this.permissionCategories.forEach(category => {
        // Ensure groupStates[category.name] is always initialized
        if (!this.localForm.permissions.groupStates) {
          this.$set(this.localForm.permissions, 'groupStates', {});
        }
        if (!this.localForm.permissions.groupStates[category.name]) {
          this.$set(this.localForm.permissions.groupStates, category.name, {});
        }
        category.groups.forEach(group => {
          const hasSelectedClaims = group.claims.some(claim => 
            this.localForm.permissions.selectedClaims.includes(claim.uuid)
          );
          this.$set(this.localForm.permissions.groupStates[category.name], group.name, hasSelectedClaims);
        });
      });
    },



    onPermissionChange() {
      // Emit both update:form and update:permissions for flexibility
      this.$emit("update:form", this.localForm);
      this.$emit("update:permissions", this.localForm.permissions);
    },

    validate() {
      // Check if at least one permission claim is selected
      return this.localForm.permissions && 
             this.localForm.permissions.selectedClaims && 
             this.localForm.permissions.selectedClaims.length > 0;
    },


  },
  watch: {
    form: {
      handler(newVal) {
        this.localForm = { ...newVal };
      },
      deep: true,
    },
    // Initialize selectedClaims when permissions data is loaded from store
    permissionCategories: {
      handler(categories) {
        if (!categories || categories.length === 0) return;
        // Extract all selected claim UUIDs from the combined permissions
        const selectedUuids = [];
        categories.forEach(cat => {
          (cat.groups || []).forEach(grp => {
            (grp.claims || []).forEach(claim => {
              if (claim.selected) {
                selectedUuids.push(claim.uuid);
              }
            });
          });
        });
        // Only initialize if we have selected claims and localForm is empty
        if (selectedUuids.length > 0 && (!this.localForm.permissions.selectedClaims || this.localForm.permissions.selectedClaims.length === 0)) {
          this.localForm.permissions.selectedClaims = selectedUuids;
          this.updateGroupStatesFromClaims();
          this.onPermissionChange();
        }
      },
      immediate: true,
      deep: true,
    },
  },
};


</script>

<style scoped>
.permissions-container {
  max-height: 76vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0;
}

.permission-card {
  background: var(--v-surface-base, #f5f5f5) !important;
  border-radius: 12px !important;
  border: 1px solid var(--v-divider-base, rgba(0,0,0,0.08)) !important;
  box-shadow: none !important;
  margin-bottom: 12px;
}

.theme--dark .permission-card {
  background: var(--v-surface-base, #2f3236) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
}

.section-title {
  color: var(--v-primary-base, #1976d2) !important;
  font-size: 1rem !important;
  line-height: 1.2 !important;
  padding-bottom: 8px !important;
}

.permission-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.permission-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--v-divider-base, rgba(0,0,0,0.04));
}

.permission-item:last-child {
  border-bottom: none;
}

.theme--dark .permission-item {
  border-bottom-color: rgba(255,255,255,0.04);
}

.permission-label {
  flex: 1;
  font-weight: 400;
  color: var(--v-text-base);
}

/* Compact switch styling */
.permission-item .v-input--switch {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
}

.permission-item .v-input--switch .v-input__control {
  min-height: auto !important;
}

.permission-item .v-input--switch .v-input__slot {
  margin: 0 !important;
}

/* Enhanced scrollbar styling */
.permissions-container::-webkit-scrollbar {
  width: 8px;
}

.permissions-container::-webkit-scrollbar-track {
  background: var(--v-background-lighten1, rgba(0,0,0,0.05));
  border-radius: 4px;
  margin: 4px;
}

.permissions-container::-webkit-scrollbar-thumb {
  background-color: var(--v-primary-lighten1, #42a5f5);
  border-radius: 4px;
  border: 1px solid var(--v-background-base);
}

.permissions-container::-webkit-scrollbar-thumb:hover {
  background-color: var(--v-primary-base, #1976d2);
}

.theme--dark .permissions-container::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.08);
}

.theme--dark .permissions-container::-webkit-scrollbar-thumb {
  background-color: rgba(255,255,255,0.3);
  border-color: var(--v-surface-base);
}

.theme--dark .permissions-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255,255,255,0.5);
}

/* Smooth scrolling behavior */
.permissions-container {
  scroll-behavior: smooth;
}

/* Ensure panels don't get cut off when expanded */
.permission-expansion-panel.v-expansion-panel--active {
  margin-bottom: 8px !important;
}

.sub-permissions {
  margin-bottom: 8px;
}

/* Expansion panel styling */
.permission-expansion-panels {
  background: transparent !important;
}

.permission-expansion-panel {
  margin-bottom: 8px !important;
  border: 1px solid rgba(0,0,0,0.12) !important;
  border-radius: 4px !important;
}

.permission-expansion-panel .v-expansion-panel-header {
  min-height: 48px !important;
  padding: 12px 16px !important;
  background: transparent !important;
}

.permission-expansion-panel .v-expansion-panel-content__wrap {
  padding: 16px !important;
  background: transparent !important;
}

.permission-expansion-panel.v-expansion-panel--active {
  border-color: #1976d2 !important;
}

/* Dark theme */
.theme--dark .permission-expansion-panel .v-expansion-panel-header {
  background: transparent !important;
}

.theme--dark .permission-expansion-panel .v-expansion-panel-content__wrap {
  background: transparent !important;
}

/* Chevron rotation animation */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.v-icon {
  transition: transform 0.3s ease;
}

/* Panel header layout */
.permission-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 1;
}

.permission-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.permission-controls {
  margin-left: auto;
}

.permission-expansion-panel {
  background: transparent !important;
  border: none !important;
  border-bottom: 1px solid var(--v-divider-base, rgba(0,0,0,0.08)) !important;
  border-radius: 0 !important;
  margin-bottom: 0 !important;
  box-shadow: none !important;
}

.permission-expansion-panel:last-child {
  border-bottom: none !important;
}

.theme--dark .permission-expansion-panel {
  border-bottom-color: rgba(255,255,255,0.08) !important;
}

.permission-panel-header {
  min-height: 48px !important;
  padding: 8px 0 !important;
  cursor: default !important;
}

.permission-panel-header:before {
  box-shadow: none !important;
}

.permission-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 1;
}

.permission-controls {
  margin-left: auto;
}

.permission-panel-content {
  padding: 0 16px 12px 16px !important;
}

/* Ensure expanded panels have proper spacing and visibility */
.permission-expansion-panel .v-expansion-panel__container {
  overflow: visible !important;
}

/* Add breathing room for the last expanded panel */
.permission-expansion-panels .permission-expansion-panel:last-child.v-expansion-panel--active {
  margin-bottom: 16px !important;
}

.sub-permissions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 16px;
}

.sub-permission-item {
  padding: 2px 0;
}

.sub-permission-checkbox {
  margin: 0 !important;
  padding: 0 !important;
}

.sub-permission-checkbox .v-input__control {
  min-height: 32px !important;
}

.sub-permission-checkbox .v-label {
  font-size: 0.75rem !important;
  color: var(--v-text-lighten1) !important;
  line-height: 1.2 !important;
}
</style>
